import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { format, isBefore, parseISO } from 'date-fns';
import { supabase } from '../lib/supabase';
import { errorMessage, formatDuration, formatTime, fullName } from '../lib/format';
import { dateKey } from '../lib/schedule';
import type { ClassRow, ClassSession, ClassType, Client, Holiday, Instructor } from '../lib/types';
import type { Recipient } from '../lib/email';
import { Modal } from '../components/Modal';
import { EmailComposeModal } from '../components/EmailComposeModal';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';

interface ClassDetail extends ClassRow {
  class_types: ClassType | null;
  instructors: { id: string; name: string } | null;
}

interface RosterEntry {
  client_id: string;
  clients: Client | null;
}

// Config for the shared compose dialog — used by "Email everyone",
// session reminders, and schedule-change notifications.
interface ComposeConfig {
  title: string;
  subject: string;
  message: string;
  allowAttachment?: boolean;
  onSent?: () => void;
}

export function ClassDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  const [cls, setCls] = useState<ClassDetail | null>(null);
  const [sessions, setSessions] = useState<ClassSession[]>([]);
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [compose, setCompose] = useState<ComposeConfig | null>(null);
  const [editingSession, setEditingSession] = useState<ClassSession | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    const [c, s, r, h] = await Promise.all([
      supabase
        .from('classes')
        .select('*, class_types(*), instructors(id, name)')
        .eq('id', id)
        .maybeSingle(),
      supabase.from('class_sessions').select('*').eq('class_id', id).order('starts_at'),
      supabase.from('enrollments').select('client_id, clients(*)').eq('class_id', id),
      supabase.from('holidays').select('*'),
    ]);
    const firstErr = c.error || s.error || r.error || h.error;
    if (firstErr) setError(errorMessage(firstErr));
    else setError(null);
    setCls((c.data as ClassDetail) ?? null);
    setSessions((s.data ?? []) as ClassSession[]);
    setRoster((r.data ?? []) as unknown as RosterEntry[]);
    setHolidays((h.data ?? []) as Holiday[]);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const label = cls ? cls.title || cls.class_types?.name || 'Class' : '';
  const color = cls?.class_types?.color ?? 'var(--brand)';
  const rosterClients = useMemo(
    () => roster.map((r) => r.clients).filter((c): c is Client => Boolean(c)),
    [roster],
  );
  const emailRecipients = useMemo<Recipient[]>(
    () =>
      rosterClients
        .filter((c) => c.email)
        .map((c) => ({ email: c.email as string, name: c.first_name })),
    [rosterClients],
  );
  const skippedNoEmail = rosterClients.length - emailRecipients.length;

  const now = new Date();
  const nextSession = useMemo(
    () =>
      sessions
        .filter((s) => s.status === 'scheduled' && !isBefore(parseISO(s.starts_at), now))
        .sort((a, b) => a.starts_at.localeCompare(b.starts_at))[0] ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessions],
  );

  // ── Roster actions ─────────────────────────────────────────────────
  async function unenroll(client: Client) {
    if (!id) return;
    const ok = await confirm({
      title: `Remove ${fullName(client)}?`,
      body: `They'll be taken off this class's roster. Their client record is kept.`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    const { error } = await supabase
      .from('enrollments')
      .delete()
      .eq('class_id', id)
      .eq('client_id', client.id);
    if (error) toast.error(errorMessage(error));
    else {
      toast.success(`${fullName(client)} removed from the class.`);
      void load();
    }
  }

  // ── Email flows ────────────────────────────────────────────────────
  function emailEveryone() {
    setCompose({
      title: `Email everyone in ${label}`,
      subject: `${label} — update`,
      message: '',
      allowAttachment: true,
    });
  }

  function sendReminder() {
    if (!nextSession) return;
    const start = parseISO(nextSession.starts_at);
    setCompose({
      title: 'Send session reminder',
      subject: `Reminder: ${label} on ${format(start, 'EEE MMM d')}`,
      message: `Just a reminder that your next ${label} session is ${format(
        start,
        'EEEE, MMMM d',
      )} at ${formatTime(nextSession.starts_at)}.\n\nPlease arrive a few minutes early, and bring your dog's usual leash and some high-value treats.\n\nSee you there!`,
      onSent: async () => {
        if (!cls) return;
        await supabase
          .from('classes')
          .update({ last_reminder_at: new Date().toISOString() })
          .eq('id', cls.id);
        void load();
      },
    });
  }

  // Offer to email the class about a change that just happened. No-op if
  // nobody has an email on file.
  function offerNotify(subject: string, message: string) {
    if (emailRecipients.length === 0) return;
    setCompose({ title: 'Notify attendees', subject, message });
  }

  async function toggleReminders() {
    if (!cls) return;
    const next = !cls.reminders_enabled;
    const { error } = await supabase
      .from('classes')
      .update({ reminders_enabled: next })
      .eq('id', cls.id);
    if (error) toast.error(errorMessage(error));
    else {
      toast.success(next ? 'Reminder prompts on.' : 'Reminder prompts off.');
      void load();
    }
  }

  // ── Session actions ────────────────────────────────────────────────
  async function cancelSession(s: ClassSession) {
    const { error } = await supabase
      .from('class_sessions')
      .update({ status: 'cancelled' })
      .eq('id', s.id);
    if (error) {
      toast.error(errorMessage(error));
      return;
    }
    toast.success('Session cancelled.');
    void load();
    const start = parseISO(s.starts_at);
    offerNotify(
      `${label} — session cancelled`,
      `Heads up: the ${format(start, 'EEEE, MMMM d')} ${label} session (${formatTime(
        s.starts_at,
      )}) has been cancelled.\n\nSorry for the short notice — reach out with any questions.`,
    );
  }

  async function restoreSession(s: ClassSession) {
    const { error } = await supabase
      .from('class_sessions')
      .update({ status: 'scheduled' })
      .eq('id', s.id);
    if (error) toast.error(errorMessage(error));
    else {
      toast.success('Session restored.');
      void load();
    }
  }

  // ── Class actions ──────────────────────────────────────────────────
  async function cancelClass() {
    if (!cls) return;
    const upcoming = sessions.filter(
      (s) => s.status === 'scheduled' && !isBefore(parseISO(s.starts_at), new Date()),
    ).length;
    const ok = await confirm({
      title: 'Cancel this class?',
      body:
        upcoming > 0
          ? `This marks the class cancelled and cancels its ${upcoming} upcoming session${upcoming === 1 ? '' : 's'} on the calendar. You can reactivate it later.`
          : 'This marks the class cancelled. You can reactivate it later.',
      confirmLabel: 'Cancel class',
      danger: true,
    });
    if (!ok) return;
    const nowIso = new Date().toISOString();
    const { error: e1 } = await supabase
      .from('classes')
      .update({ status: 'cancelled' })
      .eq('id', cls.id);
    const { error: e2 } = await supabase
      .from('class_sessions')
      .update({ status: 'cancelled' })
      .eq('class_id', cls.id)
      .eq('status', 'scheduled')
      .gte('starts_at', nowIso);
    const err = e1 || e2;
    if (err) {
      toast.error(errorMessage(err));
      return;
    }
    toast.success('Class cancelled.');
    void load();
    offerNotify(
      `${label} — cancelled`,
      `Unfortunately ${label} has been cancelled. We're very sorry for the inconvenience — please reach out with any questions and we'll help you find another class.`,
    );
  }

  async function reactivateClass() {
    if (!cls) return;
    const nowIso = new Date().toISOString();
    const { error: e1 } = await supabase
      .from('classes')
      .update({ status: 'active' })
      .eq('id', cls.id);
    const { error: e2 } = await supabase
      .from('class_sessions')
      .update({ status: 'scheduled' })
      .eq('class_id', cls.id)
      .eq('status', 'cancelled')
      .gte('starts_at', nowIso);
    const err = e1 || e2;
    if (err) toast.error(errorMessage(err));
    else toast.success('Class reactivated — upcoming sessions are back on the calendar.');
    void load();
  }

  async function deleteClass() {
    if (!cls) return;
    const ok = await confirm({
      title: 'Delete this class permanently?',
      body: `This removes the class, all ${sessions.length} of its sessions from the calendar, and its roster. Client records are kept. This can't be undone.`,
      confirmLabel: 'Delete forever',
      danger: true,
    });
    if (!ok) return;
    const { error } = await supabase.from('classes').delete().eq('id', cls.id);
    if (error) toast.error(errorMessage(error));
    else {
      toast.success('Class deleted.');
      navigate('/app/classes');
    }
  }

  // ── Render ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="card" style={{ marginTop: '0.5rem' }}>
        <span className="skel" style={{ width: '40%' }} />
        <span className="skel" style={{ width: '65%' }} />
        <span className="skel" style={{ width: '55%' }} />
      </div>
    );
  }

  if (!cls) {
    return (
      <div className="empty card">
        <span className="empty-emoji">🤔</span>
        {error ? error : "That class doesn't exist (it may have been deleted)."}
        <p style={{ marginTop: '0.6rem' }}>
          <Link to="/app/classes">← Back to all classes</Link>
        </p>
      </div>
    );
  }

  const nextStart = nextSession ? parseISO(nextSession.starts_at) : null;

  return (
    <>
      <div className="detail-topbar">
        <Link to="/app/classes" className="muted small">
          ← All classes
        </Link>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="row wrap" style={{ gap: '0.9rem', alignItems: 'flex-start' }}>
          <span className="detail-swatch" style={{ background: color }} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div className="row wrap" style={{ gap: '0.6rem' }}>
              <h1 style={{ margin: 0 }}>{label}</h1>
              {cls.status === 'cancelled' && <span className="badge badge-danger">Cancelled</span>}
            </div>
            <div className="row wrap" style={{ gap: '0.45rem', marginTop: '0.55rem' }}>
              {cls.title && cls.class_types && (
                <span className="pill">🏷️ {cls.class_types.name}</span>
              )}
              <span className="pill">🧑‍🏫 {cls.instructors?.name ?? '—'}</span>
              <span className="pill">
                ⏱ <b>{formatDuration(cls.duration_minutes)}</b>
              </span>
              <span className="pill">
                {cls.recurrence === 'weekly' ? '🔁 Weekly · ' : '📅 '}
                <b>
                  {sessions.length} session{sessions.length === 1 ? '' : 's'}
                </b>
              </span>
              <span className="pill">
                🐕 <b>{rosterClients.length}</b> enrolled
              </span>
              {cls.recurring && <span className="pill">♻️ Recurring</span>}
            </div>
          </div>
          <div className="row wrap detail-actions" style={{ justifyContent: 'flex-end' }}>
            <button
              className="btn btn-primary"
              onClick={emailEveryone}
              disabled={rosterClients.length === 0}
              title={rosterClients.length === 0 ? 'Enroll clients first' : undefined}
            >
              ✉️ Email everyone
            </button>
            <button className="btn btn-secondary" onClick={() => setEditing(true)}>
              Edit
            </button>
            {cls.status === 'active' ? (
              <button className="btn btn-ghost" onClick={() => void cancelClass()}>
                Cancel class
              </button>
            ) : (
              <button className="btn btn-secondary" onClick={() => void reactivateClass()}>
                Reactivate
              </button>
            )}
            <button
              className="btn btn-ghost"
              style={{ color: 'var(--danger)' }}
              onClick={() => void deleteClass()}
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Reminder prompt (only when the toggle is on and there's a session to remind about). */}
      {cls.status === 'active' &&
        cls.reminders_enabled &&
        nextStart &&
        emailRecipients.length > 0 && (
          <div
            className="card row between wrap"
            style={{
              borderColor: 'var(--accent)',
              background: '#fff8f0',
              marginBottom: '1rem',
              gap: '0.8rem',
            }}
          >
            <div>
              <h3 style={{ margin: 0 }}>🔔 Reminder ready</h3>
              <p className="muted small" style={{ margin: '0.2rem 0 0' }}>
                Next session {format(nextStart, 'EEE MMM d')} at{' '}
                {formatTime(nextSession!.starts_at)}.
                {cls.last_reminder_at
                  ? ` Last reminder sent ${format(parseISO(cls.last_reminder_at), 'MMM d')}.`
                  : ' No reminder sent yet.'}
              </p>
            </div>
            <div className="row">
              <button className="btn btn-primary btn-sm" onClick={sendReminder}>
                Send reminder
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => void toggleReminders()}>
                Turn off
              </button>
            </div>
          </div>
        )}

      {error && <p className="error-text">{error}</p>}

      <div className="detail-grid">
        {/* ── Sessions ── */}
        <div className="card">
          <div className="row between" style={{ marginBottom: '0.4rem' }}>
            <h2 style={{ margin: 0 }}>Sessions</h2>
            <span className="muted small">Adjust or cancel any session</span>
          </div>
          {sessions.length === 0 ? (
            <p className="muted">No sessions.</p>
          ) : (
            <div>
              {sessions.map((s, i) => {
                const start = parseISO(s.starts_at);
                const isPast = isBefore(start, now);
                const onHoliday =
                  s.status === 'scheduled' &&
                  holidays.some((h) => h.holiday_date === dateKey(start));
                return (
                  <div
                    key={s.id}
                    className={`session-row${s.status === 'cancelled' ? ' cancelled' : ''}${isPast ? ' past' : ''}`}
                  >
                    <span className="session-num">{i + 1}</span>
                    <div>
                      <div className="session-when">
                        {format(start, 'EEE MMM d, yyyy')} · {formatTime(s.starts_at)}–
                        {formatTime(s.ends_at)}
                        {s.status === 'cancelled' && (
                          <span className="badge badge-danger" style={{ marginLeft: '0.5rem' }}>
                            Cancelled
                          </span>
                        )}
                        {isPast && s.status === 'scheduled' && (
                          <span className="badge badge-success" style={{ marginLeft: '0.5rem' }}>
                            Done
                          </span>
                        )}
                        {onHoliday && (
                          <span
                            className="badge badge-warn"
                            style={{ marginLeft: '0.5rem' }}
                            title="This session falls on a no-class day — reschedule it"
                          >
                            ⚠ No-class day
                          </span>
                        )}
                      </div>
                      {s.note && <div className="session-note">{s.note}</div>}
                    </div>
                    <div className="session-actions">
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditingSession(s)}>
                        Adjust
                      </button>
                      {s.status === 'scheduled' ? (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => void cancelSession(s)}
                        >
                          Cancel
                        </button>
                      ) : (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => void restoreSession(s)}
                        >
                          Restore
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="stack">
          {/* ── Reminders ── */}
          <div className="card">
            <h2 style={{ marginBottom: '0.2rem' }}>Reminders</h2>
            <p className="muted small" style={{ marginTop: 0 }}>
              {nextStart
                ? `Next session: ${format(nextStart, 'EEE MMM d')} at ${formatTime(nextSession!.starts_at)}`
                : 'No upcoming sessions.'}
            </p>
            <div className="row wrap" style={{ marginTop: '0.5rem' }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={sendReminder}
                disabled={!nextSession || emailRecipients.length === 0}
                title={
                  !nextSession
                    ? 'No upcoming session to remind about'
                    : emailRecipients.length === 0
                      ? 'No enrolled clients have an email on file'
                      : 'Email enrolled clients about the next session'
                }
              >
                🔔 Send reminder
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => void toggleReminders()}>
                {cls.reminders_enabled ? 'Auto-prompt: On' : 'Auto-prompt: Off'}
              </button>
            </div>
            <p className="muted small" style={{ margin: '0.5rem 0 0' }}>
              {cls.last_reminder_at
                ? `Last reminder sent ${format(parseISO(cls.last_reminder_at), 'MMM d, yyyy · h:mm a')}.`
                : 'No reminder sent yet.'}
            </p>
          </div>

          {/* ── Roster ── */}
          <div className="card">
            <div className="row between" style={{ marginBottom: '0.2rem' }}>
              <h2 style={{ margin: 0 }}>Enrolled ({rosterClients.length})</h2>
              <Link to="/app/clients" className="small">
                + Add from Clients
              </Link>
            </div>
            {rosterClients.length === 0 ? (
              <p className="muted small" style={{ marginBottom: 0 }}>
                No one enrolled yet — add clients to this class from the{' '}
                <Link to="/app/clients">Clients page</Link>.
              </p>
            ) : (
              <div style={{ marginTop: '0.4rem' }}>
                {rosterClients.map((c) => (
                  <div className="roster-row" key={c.id}>
                    <span className="avatar">
                      {(c.first_name[0] ?? '').toUpperCase()}
                      {(c.last_name[0] ?? '').toUpperCase()}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{fullName(c)}</div>
                      <div className="muted small">
                        {c.dog_name || 'Dog'}
                        {c.breed ? ` · ${c.breed}` : ''}
                        {!c.email ? ' · no email' : ''}
                      </div>
                    </div>
                    {c.liability_status !== 'signed' && (
                      <span
                        className="badge badge-warn"
                        title="Liability form not signed yet — manage it on the Clients page"
                      >
                        ⚠ Waiver
                      </span>
                    )}
                    <button
                      className="btn btn-ghost btn-sm"
                      aria-label={`Remove ${fullName(c)}`}
                      onClick={() => void unenroll(c)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Notes ── */}
          <NotesCard cls={cls} onSaved={() => void load()} />
        </div>
      </div>

      {editing && (
        <EditClassForm
          cls={cls}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void load();
          }}
        />
      )}
      {compose && (
        <EmailComposeModal
          title={compose.title}
          recipients={emailRecipients}
          skippedNoEmail={skippedNoEmail}
          defaultSubject={compose.subject}
          defaultMessage={compose.message}
          allowAttachment={compose.allowAttachment}
          onClose={() => setCompose(null)}
          onSent={() => compose.onSent?.()}
        />
      )}
      {editingSession && (
        <SessionForm
          session={editingSession}
          holidays={holidays}
          onClose={() => setEditingSession(null)}
          onSaved={(change) => {
            setEditingSession(null);
            void load();
            if (change) {
              offerNotify(
                `${label} — schedule change`,
                `Heads up: the ${format(change.from, 'EEEE, MMMM d')} ${label} session has moved to ${format(
                  change.to,
                  'EEEE, MMMM d',
                )} at ${format(change.to, 'h:mm a')}.\n\nSorry for any inconvenience — see you then!`,
              );
            }
          }}
        />
      )}
    </>
  );
}

// ── Class notes card ─────────────────────────────────────────────────
function NotesCard({ cls, onSaved }: { cls: ClassDetail; onSaved: () => void }) {
  const toast = useToast();
  const [value, setValue] = useState(cls.notes ?? '');
  const [busy, setBusy] = useState(false);
  const dirty = value !== (cls.notes ?? '');

  async function save() {
    setBusy(true);
    const { error } = await supabase
      .from('classes')
      .update({ notes: value.trim() || null })
      .eq('id', cls.id);
    setBusy(false);
    if (error) toast.error(errorMessage(error));
    else {
      toast.success('Notes saved.');
      onSaved();
    }
  }

  return (
    <div className="card">
      <h2>Class notes</h2>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Anything to remember about this class — equipment, vaccination checks, who's struggling with what…"
        style={{ minHeight: 110 }}
      />
      <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.6rem' }}>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => void save()}
          disabled={!dirty || busy}
        >
          {busy ? 'Saving…' : dirty ? 'Save notes' : 'Saved'}
        </button>
      </div>
    </div>
  );
}

// ── Edit class ───────────────────────────────────────────────────────
function EditClassForm({
  cls,
  onClose,
  onSaved,
}: {
  cls: ClassDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [types, setTypes] = useState<ClassType[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [title, setTitle] = useState(cls.title ?? '');
  const [typeId, setTypeId] = useState(cls.class_type_id);
  const [instructorId, setInstructorId] = useState(cls.instructor_id);
  const [duration, setDuration] = useState(String(cls.duration_minutes));
  const [recurring, setRecurring] = useState(cls.recurring);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const [t, i] = await Promise.all([
        supabase.from('class_types').select('*').order('name'),
        supabase.from('instructors').select('*').order('name'),
      ]);
      setTypes((t.data ?? []) as ClassType[]);
      setInstructors((i.data ?? []) as Instructor[]);
    })();
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const durationMin = Math.max(5, Number(duration) || cls.duration_minutes);

    const { error } = await supabase
      .from('classes')
      .update({
        title: title.trim() || null,
        class_type_id: typeId,
        instructor_id: instructorId,
        duration_minutes: durationMin,
        recurring: cls.recurrence === 'weekly' && recurring,
      })
      .eq('id', cls.id);
    if (error) {
      setBusy(false);
      setErr(errorMessage(error));
      return;
    }

    // A new duration applies to upcoming scheduled sessions.
    if (durationMin !== cls.duration_minutes) {
      const nowIso = new Date().toISOString();
      const { data: upcoming, error: sErr } = await supabase
        .from('class_sessions')
        .select('id, starts_at')
        .eq('class_id', cls.id)
        .eq('status', 'scheduled')
        .gte('starts_at', nowIso);
      if (!sErr && upcoming) {
        for (const s of upcoming) {
          const ends = new Date(new Date(s.starts_at).getTime() + durationMin * 60_000);
          await supabase
            .from('class_sessions')
            .update({ ends_at: ends.toISOString() })
            .eq('id', s.id);
        }
      }
    }

    setBusy(false);
    toast.success('Class updated.');
    onSaved();
  }

  return (
    <Modal title="Edit class" onClose={onClose}>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="ec-title">Custom name (optional)</label>
          <input
            id="ec-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Defaults to the class type name"
          />
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="ec-type">Class type</label>
            <select id="ec-type" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="ec-instructor">Instructor</label>
            <select
              id="ec-instructor"
              value={instructorId}
              onChange={(e) => setInstructorId(e.target.value)}
            >
              {instructors.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="ec-duration">Length of each session (minutes)</label>
          <input
            id="ec-duration"
            type="number"
            min={5}
            step={5}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          />
          <p className="muted small" style={{ margin: '0.3rem 0 0' }}>
            Applies to upcoming sessions. To move dates, adjust sessions individually.
          </p>
        </div>
        {cls.recurrence === 'weekly' && (
          <div className="field">
            <label className="row" style={{ gap: '0.5rem', alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={recurring}
                onChange={(e) => setRecurring(e.target.checked)}
                style={{ marginTop: '0.2rem' }}
              />
              <span style={{ fontWeight: 400 }}>
                <strong>Recurring</strong> — when this {cls.sessions_count}-week block finishes,
                automatically start another {cls.sessions_count}-week block (same roster, same day
                and time), instead of needing a brand-new class.
              </span>
            </label>
          </div>
        )}
        {err && <p className="error-text">{err}</p>}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.6rem' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Reschedule / annotate one session ────────────────────────────────
function SessionForm({
  session,
  holidays,
  onClose,
  onSaved,
}: {
  session: ClassSession;
  holidays: Holiday[];
  onClose: () => void;
  // Reports back whether the session actually moved, so the parent can
  // offer to notify attendees.
  onSaved: (change?: { from: Date; to: Date }) => void;
}) {
  const toast = useToast();
  const start = parseISO(session.starts_at);
  const durationMs = parseISO(session.ends_at).getTime() - start.getTime();
  const [date, setDate] = useState(format(start, 'yyyy-MM-dd'));
  const [time, setTime] = useState(format(start, 'HH:mm'));
  const [note, setNote] = useState(session.note ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const holiday = holidays.find((h) => h.holiday_date === date);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const newStart = new Date(`${date}T${time}`);
    if (Number.isNaN(newStart.getTime())) return;
    setBusy(true);
    setErr(null);
    const { error } = await supabase
      .from('class_sessions')
      .update({
        starts_at: newStart.toISOString(),
        ends_at: new Date(newStart.getTime() + durationMs).toISOString(),
        note: note.trim() || null,
      })
      .eq('id', session.id);
    setBusy(false);
    if (error) setErr(errorMessage(error));
    else {
      toast.success('Session updated.');
      const moved = newStart.getTime() !== start.getTime();
      onSaved(moved ? { from: start, to: newStart } : undefined);
    }
  }

  return (
    <Modal title="Adjust session" onClose={onClose}>
      <form onSubmit={submit}>
        <div className="field-row">
          <div className="field">
            <label htmlFor="ss-date">Date</label>
            <input
              id="ss-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="ss-time">Start time</label>
            <input
              id="ss-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              required
            />
          </div>
        </div>
        {holiday && (
          <p className="error-text" style={{ marginTop: '-0.3rem' }}>
            Heads up: that day is marked "{holiday.label}".
          </p>
        )}
        <div className="field">
          <label htmlFor="ss-note">Session note (optional)</label>
          <textarea
            id="ss-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder='e.g. "Moved from Tuesday — arena was booked"'
            style={{ minHeight: 70 }}
          />
        </div>
        <p className="muted small" style={{ marginTop: '-0.3rem' }}>
          Only this session moves — the rest of the series stays put. If the date or time changes,
          you'll be offered a note to email attendees.
        </p>
        {err && <p className="error-text">{err}</p>}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.6rem' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save session'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
